import type {
  ICourtSchedule,
  IFreeSlotItem,
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
} from '../../../../../types/schedule';
import { isFreeSlot, isSameSlot } from '../../../../../types/schedule';
import type { Court } from '../../../../../types/tournament';
import {
  getMatchDurationFromConstraints,
  getTournamentDayDisplayBounds,
} from '../schedule.helpers';

import {
  computeValidPlacementStartTimesMs,
  type PlacementHighlightMatchRange,
} from './scheduleGridPlacementHighlight';
import { getMatchEndMs } from './scheduleMatchEndTime';

export const PLACEMENT_KEYBOARD_SLOT_MINUTES = 5;
export const PLACEMENT_KEYBOARD_MS_PER_MINUTE = 60_000;

export type PlacementKeyboardCourt = {
  court: Court;
  validStartTimesMs: number[];
};

export type PlacementKeyboardDay = {
  dayKey: number;
  dayStartMs: number;
  courts: PlacementKeyboardCourt[];
};

export type PlacementKeyboardContext = {
  slotMinutes: number;
  msPerMinute: number;
  days: PlacementKeyboardDay[];
};

export type PlacementKeyboardDirection = 'up' | 'down' | 'left' | 'right';

export function slotStartMsFromFreeSlot(
  slot: IFreeSlotItem,
  dayStartMs: number
): number {
  const startMs = new Date(slot.scheduleDate).getTime();
  if (!Number.isFinite(startMs)) return dayStartMs;
  return startMs;
}

export function buildFreeSlotAtStartMs(
  dayKey: number,
  court: Court,
  startMs: number,
  slotMinutes: number
): IFreeSlotItem {
  return {
    isFreeSlot: true,
    day: dayKey,
    court,
    scheduleDate: new Date(startMs).toISOString(),
    duration: slotMinutes,
  };
}

export function findDayContext(
  context: PlacementKeyboardContext,
  dayKey: number
): PlacementKeyboardDay | undefined {
  return context.days.find((d) => d.dayKey === dayKey);
}

export function isSlotValidInContext(
  slot: IFreeSlotItem,
  context: PlacementKeyboardContext
): boolean {
  const day = findDayContext(context, slot.day);
  if (!day) return false;
  const courtEntry = day.courts.find((c) => c.court.id === slot.court.id);
  if (!courtEntry) return false;
  const startMs = slotStartMsFromFreeSlot(slot, day.dayStartMs);
  return courtEntry.validStartTimesMs.includes(startMs);
}

export function seedFocusedPlacementSlot(
  context: PlacementKeyboardContext
): IFreeSlotItem | null {
  for (const day of context.days) {
    for (const courtEntry of day.courts) {
      const firstStart = courtEntry.validStartTimesMs[0];
      if (firstStart == null) continue;
      return buildFreeSlotAtStartMs(
        day.dayKey,
        courtEntry.court,
        firstStart,
        context.slotMinutes
      );
    }
  }
  return null;
}

/** Prefer the match's current grid slot when moving a scheduled match; else earliest valid slot. */
export function seedFocusedPlacementSlotForScheduledMatch(
  context: PlacementKeyboardContext,
  match: IMatchScheduleItem
): IFreeSlotItem | null {
  if (!match.scheduleDate || !match.court) {
    return seedFocusedPlacementSlot(context);
  }

  const startMs = new Date(match.scheduleDate).getTime();
  for (const day of context.days) {
    const courtEntry = day.courts.find((c) => c.court.id === match.court.id);
    if (!courtEntry) continue;
    const slot = buildFreeSlotAtStartMs(
      day.dayKey,
      match.court,
      startMs,
      context.slotMinutes
    );
    if (isSlotValidInContext(slot, context)) return slot;
  }

  return seedFocusedPlacementSlot(context);
}

export function getNextFocusedSlot(
  direction: PlacementKeyboardDirection,
  current: IFreeSlotItem,
  context: PlacementKeyboardContext
): IFreeSlotItem {
  const day = findDayContext(context, current.day);
  if (!day) return current;

  const courtIndex = day.courts.findIndex((c) => c.court.id === current.court.id);
  if (courtIndex < 0) return current;

  const courtEntry = day.courts[courtIndex];
  const startMs = slotStartMsFromFreeSlot(current, day.dayStartMs);
  const stepMs = context.slotMinutes * context.msPerMinute;

  if (direction === 'up' || direction === 'down') {
    const times = courtEntry.validStartTimesMs;
    const idx = times.indexOf(startMs);
    if (idx < 0) return current;
    const nextIdx = direction === 'down' ? idx + 1 : idx - 1;
    const nextStart = times[nextIdx];
    if (nextStart == null) return current;
    return buildFreeSlotAtStartMs(day.dayKey, courtEntry.court, nextStart, context.slotMinutes);
  }

  const delta = direction === 'right' ? 1 : -1;
  for (
    let i = courtIndex + delta;
    i >= 0 && i < day.courts.length;
    i += delta
  ) {
    const target = day.courts[i];
    if (target.validStartTimesMs.includes(startMs)) {
      return buildFreeSlotAtStartMs(day.dayKey, target.court, startMs, context.slotMinutes);
    }
  }
  return current;
}

export type BuildPlacementKeyboardContextParams = {
  days: {
    dayKey: number;
    dayStartMs: number;
    dayEndMs: number;
    courts: {
      court: Court;
      matchRanges: PlacementHighlightMatchRange[];
    }[];
  }[];
  activeMatch: IMatchScheduleItem;
  durationMins: number;
  slotMinutes?: number;
  msPerMinute?: number;
};

export function buildPlacementKeyboardContext({
  days,
  activeMatch,
  durationMins,
  slotMinutes = PLACEMENT_KEYBOARD_SLOT_MINUTES,
  msPerMinute = PLACEMENT_KEYBOARD_MS_PER_MINUTE,
}: BuildPlacementKeyboardContextParams): PlacementKeyboardContext {
  return {
    slotMinutes,
    msPerMinute,
    days: days.map((day) => ({
      dayKey: day.dayKey,
      dayStartMs: day.dayStartMs,
      courts: day.courts.map((courtRow) => ({
        court: courtRow.court,
        validStartTimesMs: computeValidPlacementStartTimesMs({
          dayStartMs: day.dayStartMs,
          dayEndMs: day.dayEndMs,
          matchRanges: courtRow.matchRanges,
          activeMatch,
          durationMins,
          slotMinutes,
          msPerMinute,
        }),
      })),
    })),
  };
}

export function slotsEqual(a: IFreeSlotItem | null, b: IFreeSlotItem | null): boolean {
  if (a == null || b == null) return a === b;
  return isSameSlot(a, b);
}

export type PlacementInFlightGate = {
  isInFlight: () => boolean;
  tryAcquire: () => boolean;
  release: () => void;
};

export function createPlacementInFlightGate(): PlacementInFlightGate {
  let inFlight = false;
  return {
    isInFlight(): boolean {
      return inFlight;
    },
    tryAcquire(): boolean {
      if (inFlight) return false;
      inFlight = true;
      return true;
    },
    release(): void {
      inFlight = false;
    },
  };
}

export function buildPlacementMatchRangesForCourt(
  courtSchedule: ICourtSchedule,
  dayEndMs: number
): PlacementHighlightMatchRange[] {
  return courtSchedule.matches
    .filter((item): item is IMatchScheduleItem => !isFreeSlot(item) && !!item.scheduleDate)
    .map((item) => {
      const startMs = new Date(item.scheduleDate!).getTime();
      return {
        item,
        startMs,
        endMs: getMatchEndMs(item, startMs, dayEndMs, PLACEMENT_KEYBOARD_MS_PER_MINUTE),
      };
    });
}

export function buildPlacementKeyboardContextFromSchedule(
  displaySchedule: ITournamentSchedule,
  scheduleConstraints: IScheduleConstraints,
  activeMatch: IMatchScheduleItem,
  timeZone: string,
  occupancySchedule?: ITournamentSchedule | null
): PlacementKeyboardContext {
  const durationMins =
    activeMatch.duration ||
    getMatchDurationFromConstraints(activeMatch, scheduleConstraints) ||
    30;

  const days = displaySchedule.days.map((day) => {
    const dayConstraints = scheduleConstraints.days.find((d) => d.dayKey === day.dayKey);
    const scheduledOnDay = day.courts
      .flatMap((court) => court.matches)
      .filter((item): item is IMatchScheduleItem => !isFreeSlot(item) && !!item.scheduleDate);
    const fallbackStart = scheduledOnDay.length
      ? Math.min(...scheduledOnDay.map((item) => new Date(item.scheduleDate!).getTime()))
      : Date.now();
    const fallbackEnd = scheduledOnDay.length
      ? Math.max(
          ...scheduledOnDay.map((item) => {
            const startMs = new Date(item.scheduleDate!).getTime();
            return getMatchEndMs(
              item,
              startMs,
              Number.MAX_SAFE_INTEGER,
              PLACEMENT_KEYBOARD_MS_PER_MINUTE
            );
          })
        )
      : fallbackStart + 60 * PLACEMENT_KEYBOARD_MS_PER_MINUTE;

    let dayStartMs = fallbackStart;
    let dayEndMs = fallbackEnd;
    if (dayConstraints) {
      const displayBounds = getTournamentDayDisplayBounds(
        dayConstraints,
        scheduledOnDay,
        scheduleConstraints,
        timeZone
      );
      dayStartMs = displayBounds.effectiveStartMs;
      dayEndMs = displayBounds.effectiveEndMs;
    }

    const occupancyDay = occupancySchedule?.days.find((d) => d.dayKey === day.dayKey);

    return {
      dayKey: day.dayKey,
      dayStartMs,
      dayEndMs,
      courts: day.courts.map((courtSchedule) => {
        const occupancyCourt = occupancyDay?.courts.find(
          (c) => c.court.id === courtSchedule.court.id
        );
        const rangesSource = occupancyCourt ?? courtSchedule;
        return {
          court: courtSchedule.court,
          matchRanges: buildPlacementMatchRangesForCourt(rangesSource, dayEndMs),
        };
      }),
    };
  });

  return buildPlacementKeyboardContext({
    days,
    activeMatch,
    durationMins,
  });
}
